import { getConfiguredGoogleMapID } from './googleMapConfig.web';
import { getGoogleMapColorScheme, getGoogleMapStyles } from './googleMapTheme.web';
import type { NearbyVenueResult } from '@/features/map/useNearbyVenues';
import { getDealTiming } from '@/features/venues/deals/deals';
import { getVenueMapCategoryConfig } from '@/features/venues/venueCategories';
import { useAppearance } from '@/lib/design/appearance';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { locationStore, useLocation } from '@/lib/location/location';
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
  type Cluster,
  type ClusterStats,
  type Renderer,
} from '@googlemaps/markerclusterer';
import { AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import * as React from 'react';

type WebMapRegion = {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
};

type MapIdleEvent = {
  map: any;
};

function isValidVenueCoordinate(venue: NearbyVenueResult) {
  return (
    Number.isFinite(venue.lat) &&
    Number.isFinite(venue.lng) &&
    Math.abs(venue.lat) <= 90 &&
    Math.abs(venue.lng) <= 180
  );
}

const AMBER = '#F59E0B';
const AMBER_TEXT = '#1A0F00';
const PIN_BORDER = '#0A0A0A';
const VERIFIED_ORANGE = '#EF4E2E';
const DEAL_ORANGE = '#FB923C';
const LIVE_GREEN = 'rgba(44,207,143,1)';
declare const google: any;
type GoogleAdvancedMarker = any;

function registerMarkerClick(marker: GoogleAdvancedMarker, onClick: (event: any) => void) {
  const listener = marker.addListener('click', onClick);
  return () => listener.remove();
}

const venueClusterRenderer: Renderer = {
  render(cluster: Cluster, _stats: ClusterStats) {
    const { count, position } = cluster;
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position,
      zIndex: 50,
    });
    const size = count >= 50 ? 68 : count >= 25 ? 58 : count >= 10 ? 48 : 40;
    const div = document.createElement('div');
    div.style.cssText = `
      width:${size}px;
      height:${size}px;
      border-radius:${size / 2}px;
      display:flex;
      align-items:center;
      justify-content:center;
      background:${count >= 25 ? '#A93A1F' : '#C94C2D'};
      border:3px solid ${count >= 25 ? '#F2A38F' : '#F9C8BB'};
      box-shadow:0 8px 18px rgba(201,76,45,0.34), 0 4px 14px rgba(0,0,0,0.36);
      color:#fff;
      cursor:pointer;
      font-size:${count >= 25 ? 17 : 13}px;
      font-weight:800;
      line-height:1;
    `;
    div.textContent = count >= 100 ? '99+' : String(count);
    marker.content = div;
    return marker;
  },
};

export function VenueMap({
  venues,
  selectedId,
  initialRegion,
  onSelect,
  onMapMove,
  onRegionChangeComplete,
  onRecenter,
}: {
  venues: NearbyVenueResult[];
  selectedId?: string | null;
  initialRegion?: WebMapRegion;
  onSelect: (venue: NearbyVenueResult) => void;
  onMapMove?: () => void;
  onRegionChangeComplete?: (region: WebMapRegion) => void;
  onRecenter?: (fn: () => void) => void;
}) {
  const resolvedMode = useAppearance((state) => state.resolvedMode);
  const mapId = getConfiguredGoogleMapID();
  const { coords } = useLocation();
  const userCenter = React.useMemo(
    () => ({ lat: coords.latitude, lng: coords.longitude }),
    [coords.latitude, coords.longitude]
  );
  const defaultCenter = React.useMemo(() => {
    if (initialRegion) {
      return { lat: initialRegion.latitude, lng: initialRegion.longitude };
    }
    const { coords: currentCoords } = locationStore.getState();
    return { lat: currentCoords.latitude, lng: currentCoords.longitude };
  }, [initialRegion]);

  const handleIdle = React.useCallback(
    (event: MapIdleEvent) => {
      const center = event.map.getCenter()?.toJSON();
      const bounds = event.map.getBounds()?.toJSON();
      if (!center || !bounds) {
        return;
      }
      onRegionChangeComplete?.({
        latitude: center.lat,
        longitude: center.lng,
        latitudeDelta: Math.max(0.01, Math.abs(bounds.north - bounds.south)),
        longitudeDelta: Math.max(0.01, Math.abs(bounds.east - bounds.west)),
      });
    },
    [onRegionChangeComplete]
  );

  return (
    <Map
      defaultCenter={defaultCenter}
      defaultZoom={13}
      backgroundColor={resolvedMode === 'night' ? '#080808' : '#fff8e7'}
      colorScheme={getGoogleMapColorScheme(resolvedMode)}
      mapId={mapId}
      renderingType="VECTOR"
      gestureHandling="greedy"
      clickableIcons={false}
      disableDefaultUI
      styles={mapId ? undefined : getGoogleMapStyles(resolvedMode)}
      style={{ width: '100%', height: '100%' }}
      onIdle={handleIdle as any}
      onClick={() => onMapMove?.()}>
      <AdvancedMarker position={userCenter} zIndex={100}>
        <div style={{ position: 'relative', width: 20, height: 20 }}>
          <div
            style={{
              position: 'absolute',
              inset: -6,
              borderRadius: '50%',
              backgroundColor: 'rgba(66,133,244,0.18)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: -2,
              borderRadius: '50%',
              backgroundColor: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              backgroundColor: '#4285F4',
            }}
          />
        </div>
      </AdvancedMarker>

      <VenueMarkerLayer
        venues={venues}
        selectedId={selectedId}
        onSelect={onSelect}
      />
    </Map>
  );
}

function VenueMarkerLayer({
  venues,
  selectedId,
  onSelect,
}: {
  venues: NearbyVenueResult[];
  selectedId?: string | null;
  onSelect: (venue: NearbyVenueResult) => void;
}) {
  const map = useMap();
  const renderedVenues = React.useMemo(() => venues.filter(isValidVenueCoordinate), [venues]);
  const [clusterer, setClusterer] = React.useState<MarkerClusterer | null>(null);
  const markersRef = React.useRef<globalThis.Map<string, GoogleAdvancedMarker>>(
    new globalThis.Map()
  );
  const onSelectRef = React.useRef(onSelect);

  React.useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  React.useEffect(() => {
    if (!map) return;
    const nextClusterer = new MarkerClusterer({
      map,
      renderer: venueClusterRenderer,
      algorithm: new SuperClusterAlgorithm({ radius: 96, maxZoom: 14 }),
      onClusterClick: (event, cluster, clusterMap) => {
        event.domEvent?.stopPropagation?.();
        if (cluster.bounds) {
          clusterMap.fitBounds(cluster.bounds);
        }
      },
    });
    setClusterer(nextClusterer);
    return () => {
      nextClusterer.clearMarkers();
      setClusterer(null);
    };
  }, [map]);

  React.useEffect(() => {
    if (!map || !selectedId) return;
    const venue = renderedVenues.find((item) => item.id === selectedId);
    if (!venue) return;
    map.panTo({ lat: venue.lat, lng: venue.lng });
    map.setZoom(15);
  }, [map, renderedVenues, selectedId]);

  React.useEffect(() => {
    if (!clusterer) return;
    const markerMap = markersRef.current;
    const activeVenueIDs = new Set(renderedVenues.map((venue) => venue.id));

    markerMap.forEach((marker, venueID) => {
      if (!activeVenueIDs.has(venueID)) {
        clusterer.removeMarker(marker);
        marker.__radiousRemoveClick?.();
        markerMap.delete(venueID);
      }
    });

    renderedVenues.forEach((venue) => {
      const markerState = getVenueMarkerState(venue, venue.id === selectedId);
      const existingMarker = markerMap.get(venue.id);
      if (existingMarker) {
        existingMarker.position = markerState.position;
        existingMarker.zIndex = markerState.zIndex;
        if (existingMarker.__radiousVisualKey !== markerState.visualKey) {
          existingMarker.content = markerState.content;
          existingMarker.__radiousVisualKey = markerState.visualKey;
        }
        return;
      }

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: markerState.position,
        content: markerState.content,
        zIndex: markerState.zIndex,
      });
      marker.__radiousVisualKey = markerState.visualKey;
      marker.__radiousRemoveClick = registerMarkerClick(marker, (clickEvent) => {
        clickEvent?.stop?.();
        clickEvent?.domEvent?.stopPropagation?.();
        onSelectRef.current(venue);
      });
      markerMap.set(venue.id, marker);
      clusterer.addMarker(marker);
    });

    clusterer.render();
  }, [clusterer, renderedVenues, selectedId]);

  React.useEffect(() => {
    const markerMap = markersRef.current;
    return () => {
      markerMap.forEach((marker) => {
        marker.__radiousRemoveClick?.();
      });
      markerMap.clear();
    };
  }, []);

  return null;
}

function getVenueMarkerState(venue: NearbyVenueResult, selected: boolean) {
  return {
    content: createVenuePinElement(venue, selected),
    position: { lat: venue.lat, lng: venue.lng },
    zIndex: selected ? 40 : venue.has_live_event ? 20 : 5,
    visualKey: [
      selected ? 'selected' : 'idle',
      venue.logo_url,
      venue.category,
      venue.name,
      String(venue.upcoming_event_count),
      String(venue.has_live_event),
      String(getDealCount(venue.deals)),
      String(getLiveDealCount(venue.deals)),
      String(venue.is_verified),
    ].join('|'),
  };
}

function createVenuePinElement(venue: NearbyVenueResult, selected: boolean) {
  const config = getVenueMapCategoryConfig(venue.category);
  const hasUpcomingEvents = venue.upcoming_event_count > 0;
  const hasDeals = getDealCount(venue.deals) > 0;
  const hasLiveDeals = getLiveDealCount(venue.deals) > 0;
  const size = selected ? 48 : 44;
  const containerSize = size + 26;
  const wrap = document.createElement('div');
  wrap.style.cssText = `
    position:relative;
    width:${containerSize}px;
    height:${containerSize}px;
    display:grid;
    place-items:center;
    cursor:pointer;
    transform:${selected ? 'scale(1.06)' : 'scale(1)'};
    transition:transform 140ms ease;
  `;

  if (venue.has_live_event) {
    const glow = document.createElement('div');
    const glowSize = size + 12;
    const glowInset = (containerSize - glowSize) / 2;
    glow.style.cssText = `
      position:absolute;
      top:${glowInset}px;
      left:${glowInset}px;
      width:${glowSize}px;
      height:${glowSize}px;
      border-radius:${glowSize / 2}px;
      border:2px solid rgba(44,207,143,0.45);
      background:rgba(44,207,143,0.08);
      animation:venue-live-pulse 1800ms ease-in-out infinite;
    `;
    wrap.appendChild(glow);
  }

  if (selected) {
    const ringSize = size + 8;
    const ring = document.createElement('div');
    ring.style.cssText = `
      position:absolute;
      top:${(containerSize - ringSize) / 2}px;
      left:${(containerSize - ringSize) / 2}px;
      width:${ringSize}px;
      height:${ringSize}px;
      border-radius:${ringSize / 2}px;
      border:2px solid rgba(255,255,255,0.9);
    `;
    wrap.appendChild(ring);
  }

  const pin = document.createElement('div');
  pin.style.cssText = `
    width:${size}px;
    height:${size}px;
    border-radius:${size / 2}px;
    display:flex;
    align-items:center;
    justify-content:center;
    overflow:hidden;
    background:#${config.bgColor};
    border:${selected ? 3 : 2}px solid ${selected ? '#FFFFFF' : venue.has_live_event ? LIVE_GREEN : `#${config.color}`};
    box-shadow:0 ${venue.has_live_event ? 6 : 3}px 12px rgba(0,0,0,0.42), 0 0 8px ${venue.has_live_event ? LIVE_GREEN : `#${config.color}`};
    z-index:1;
  `;

  if (venue.logo_url) {
    const img = document.createElement('img');
    img.alt = '';
    img.draggable = false;
    img.src = venue.logo_url;
    img.style.cssText = `
      width:${size - 8}px;
      height:${size - 8}px;
      border-radius:${(size - 8) / 2}px;
      object-fit:cover;
    `;
    pin.appendChild(img);
  } else {
    const label = document.createElement('span');
    label.textContent = venue.name.trim().slice(0, 1).toUpperCase() || 'R';
    label.style.cssText = `
      color:#${config.color};
      font-size:16px;
      font-weight:900;
    `;
    pin.appendChild(label);
  }
  wrap.appendChild(pin);

  if (hasUpcomingEvents) {
    const badge = createVenueBadge(String(Math.min(venue.upcoming_event_count, 99)), AMBER, AMBER_TEXT);
    badge.style.top = '6px';
    badge.style.right = '6px';
    wrap.appendChild(badge);
  }

  if (hasDeals) {
    const badge = createVenueBadge('$', hasLiveDeals ? DEAL_ORANGE : '#14100C', hasLiveDeals ? PIN_BORDER : DEAL_ORANGE);
    badge.style.top = `${containerSize / 2 - 10}px`;
    badge.style.right = '2px';
    wrap.appendChild(badge);
  }

  if (venue.is_verified) {
    const badge = createVenueBadge('✓', '#FFFFFF', VERIFIED_ORANGE);
    badge.style.bottom = '6px';
    badge.style.right = '6px';
    wrap.appendChild(badge);
  }

  if (selected) {
    const label = document.createElement('div');
    label.style.cssText = `
      position:absolute;
      top:${containerSize - 2}px;
      left:50%;
      max-width:170px;
      transform:translateX(-50%);
      padding:5px 9px;
      border-radius:999px;
      border:1px solid ${DESIGN_TOKENS.colors.accent};
      background:rgba(8,8,8,0.9);
      color:#fff;
      box-shadow:0 10px 24px rgba(0,0,0,0.36);
      z-index:5;
      overflow:hidden;
      text-overflow:ellipsis;
      white-space:nowrap;
      font-size:12px;
      font-weight:800;
    `;
    label.textContent = venue.name;
    wrap.appendChild(label);
  }

  return wrap;
}

function createVenueBadge(label: string, backgroundColor: string, textColor: string) {
  const badge = document.createElement('div');
  badge.textContent = label;
  badge.style.cssText = `
    position:absolute;
    min-width:20px;
    height:20px;
    border-radius:10px;
    display:flex;
    align-items:center;
    justify-content:center;
    padding:0 4px;
    background:${backgroundColor};
    border:2px solid ${PIN_BORDER};
    color:${textColor};
    font-size:11px;
    font-weight:900;
    line-height:1;
    z-index:4;
  `;
  return badge;
}

function getLiveDealCount(deals?: NearbyVenueResult['deals']) {
  return deals?.filter((deal) => getDealTiming(deal).state === 'live').length ?? 0;
}

function getDealCount(deals?: NearbyVenueResult['deals']) {
  return (
    deals?.filter((deal) => {
      const state = getDealTiming(deal).state;
      return state === 'live' || state === 'scheduled';
    }).length ?? 0
  );
}
